import { initializeApp, getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

const firebaseConfig = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

function initAppIfNeeded() {
    // If no API key is present (e.g., during build) avoid initializing
    if (!firebaseConfig.apiKey) return null;
    if (getApps().length) return getApps()[0];
    try {
        return initializeApp(firebaseConfig);
    } catch (err) {
        // Guard against initialization errors during build or misconfiguration
        // and return null so server-side code doesn't crash the build.
        // We log a warning to make debugging easier when running locally.
        // eslint-disable-next-line no-console
        console.warn('Firebase initialization skipped or failed:', err);
        return null;
    }
}

// Only initialize in the browser (client-side). During server builds this will be null.
const app = typeof window !== 'undefined' ? initAppIfNeeded() : null;

export const auth = app ? getAuth(app) : null;
export const db = app ? getFirestore(app) : null;
export const storage = app ? getStorage(app) : null;

/**
 * Returns a Firestore instance or throws if not initialized. Use this where compile-time
 * Firestore is required (e.g., `collection`, `doc`, `writeBatch`) to avoid repetitive
 * null-guards across the codebase.
 */
export function requireDb(): Firestore {
    if (!db) throw new Error('Firestore is not initialized.');
    return db;
}
